import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbDatabase, TbFileText } from "solid-icons/tb";
import { createMemo, For, Match, Show, Switch } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { NotionIcon } from "./notionIcon";
import { type NotionSearchResultsPayload, parseNotionSearchResultsPayload } from "./notionSearchResultsSchema";

interface NotionSearchResultsRendererProps {
  block: StreamingBlock;
}

// Notion page/database URLs arrive from tool output and are untrusted; only
// https URLs are safe to put in an href.
const safeNotionUrl = (url: string | undefined): string | undefined => (url?.startsWith("https://") ? url : undefined);

export const NotionSearchResultsRenderer = (props: NotionSearchResultsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<NotionSearchResultsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseNotionSearchResultsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("notion_search_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().results.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <NotionIcon class="size-4 shrink-0" />
                <span class="truncate text-sm font-medium text-primary-text">{rt.t("notion_search_card.title")}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().results.length === 1
                    ? rt.t("notion_search_card.results_one")
                    : rt.t("notion_search_card.results", { count: String(data().results.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().results}>
                  {(result) => (
                    <li class="flex items-center gap-2 px-3 py-2">
                      <Switch fallback={<TbFileText class="size-4 shrink-0 text-muted-text" aria-hidden="true" />}>
                        <Match when={result.type === "database"}>
                          <TbDatabase class="size-4 shrink-0 text-muted-text" aria-hidden="true" />
                        </Match>
                      </Switch>
                      <Show
                        when={safeNotionUrl(result.url)}
                        fallback={
                          <span class="truncate text-sm text-primary-text">
                            {result.title || rt.t("notion_search_card.untitled")}
                          </span>
                        }
                      >
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="truncate text-sm text-brand hover:underline"
                          >
                            {result.title || rt.t("notion_search_card.untitled")}
                          </a>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
